/**
 * Median time-to-restore per service — the one panel on this board that looks
 * backwards.
 *
 * Everything else here is a snapshot: the tiles say what is broken now, the
 * resolved timeline says what closed this week. Neither answers "is Exchange
 * getting worse?", which is the question that decides whether a service is
 * worth escalating to Microsoft. This panel answers it from the issue archive
 * the backend already fetches on every refresh and previously discarded down to
 * seven days — so it costs no extra Graph call and, per NOVA's live-poll rule,
 * persists nothing. The history belongs to Graph.
 *
 * ## The three ways this number can lie, and what stops each
 *
 * 1. **An open issue counted as instant.** An unresolved issue has no end
 *    timestamp. The backend excludes it and reports the count; a "still open"
 *    column shows it, so a service with a running outage cannot read as fast.
 * 2. **A confident median from n=1.** Every row states its sample size, and a
 *    row below `min_sample_size` is captioned as an anecdote rather than a
 *    trend. It is still shown — a single fourteen-hour outage is worth seeing —
 *    but it is not dressed up as a measurement.
 * 3. **An unmeasurable duration shown as zero.** `median_minutes: null` renders
 *    as an em dash, never `0 min`, and the row says how many resolved issues
 *    were dropped for unusable timestamps.
 *
 * ## Altitude (L1)
 *
 * One aggregate row per service, worst median first — a ranking, not an entity
 * list, which is why a grid is the right form here and not a table rendered too
 * high. The rows are already sorted by the backend; the grid's own sorting lets
 * a reader re-rank by sample size when they want to know how much to trust it.
 */

import { Card, DataGrid, EmptyState, Typography } from "@norican/react";

import type { M365Mttr, M365ServiceMttr } from "@/lib/api";

import { formatDateTime, formatMinutes } from "./status";

interface MttrPanelProps {
  mttr: M365Mttr;
}

export function MttrPanel({ mttr }: MttrPanelProps) {
  const measured = mttr.services.filter((row) => row.median_minutes !== null);

  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2">
            Time to restore
          </Typography>
          <Typography variant="body-sm" tone="muted">
            {coverageCaption(mttr)}
          </Typography>
        </div>
      </Card.Header>
      <Card.Body padding={mttr.services.length === 0 ? "md" : "none"}>
        {mttr.services.length === 0 ? (
          <EmptyState
            size="sm"
            title="No incident history read"
            description="Microsoft Graph returned no service-health issues for this tenant on the last refresh, so there is nothing to measure. This is an absence of history, not a tenant that recovers instantly."
          />
        ) : (
          <DataGrid<M365ServiceMttr>
            aria-label="Median time to restore per Microsoft 365 service"
            data={mttr.services}
            getRowId={(row) => row.service}
            getRowSearchText={(row) => row.service}
            density="comfortable"
            columns={[
              {
                id: "service",
                header: "Service",
                accessor: (row) => row.service,
                sortable: true,
                cell: (row) => <span className="text-fg text-sm font-medium">{row.service}</span>,
              },
              {
                id: "median",
                header: "Median time to restore",
                align: "end",
                width: 190,
                // Sorting on the raw minutes, so a null row sorts as absent
                // rather than as the fastest service in the tenant.
                accessor: (row) => row.median_minutes,
                sortable: true,
                cell: (row) => (
                  <span className="text-fg tabular-nums">{formatMinutes(row.median_minutes)}</span>
                ),
              },
              {
                id: "sample",
                header: "Sample",
                align: "end",
                width: 210,
                accessor: (row) => row.sample_size,
                sortable: true,
                cell: (row) => (
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-fg-muted text-sm tabular-nums">
                      {row.sample_size} resolved
                    </span>
                    <span className="text-fg-muted text-xs">{sampleCaption(row)}</span>
                  </div>
                ),
              },
              {
                id: "period",
                header: "Covering",
                width: 200,
                accessor: (row) => row.earliest_resolved_at,
                sortable: true,
                cell: (row) => <span className="text-fg-muted text-xs">{periodCaption(row)}</span>,
              },
              {
                id: "excluded",
                header: "Left out",
                align: "end",
                width: 170,
                accessor: (row) => row.excluded_open + row.excluded_undated,
                sortable: true,
                cell: (row) => <span className="text-fg-muted text-xs">{exclusions(row)}</span>,
              },
            ]}
          />
        )}
      </Card.Body>
      {mttr.services.length > 0 && (
        <Card.Footer>
          <Typography variant="caption" as="p">
            Median, not mean, so one long advisory cannot flatter or wreck a service&apos;s figure.
            Computed over the {mttr.issues_considered}{" "}
            {mttr.issues_considered === 1 ? "issue" : "issues"} in the archive Microsoft returned on
            this refresh — a wider window than the resolved list above, and {measured.length} of{" "}
            {mttr.services.length} {mttr.services.length === 1 ? "service" : "services"} had enough
            timestamps to measure. Nothing is stored: the sample is whatever Graph serves.
          </Typography>
        </Card.Footer>
      )}
    </Card>
  );
}

/** The header's one-line answer to "how much is this table standing on?". */
function coverageCaption(mttr: M365Mttr): string {
  if (mttr.sample_size === 0) {
    return "No usable durations";
  }
  const trend = mttr.services.filter((row) => row.is_meaningful).length;
  return `${mttr.sample_size} resolved incidents · ${trend} service${trend === 1 ? "" : "s"} with a usable trend`;
}

/**
 * Says out loud whether a row's median is a trend or an anecdote. A median from
 * one incident is printed beside one from thirty in the same column, so the
 * only thing keeping them apart is this caption.
 */
function sampleCaption(row: M365ServiceMttr): string {
  if (row.sample_size === 0) {
    return "not measurable yet";
  }
  return row.is_meaningful ? "enough for a trend" : "too few to be a trend";
}

/** Which stretch of history a row's median is drawn from. */
function periodCaption(row: M365ServiceMttr): string {
  if (row.earliest_resolved_at === null || row.latest_resolved_at === null) {
    return "—";
  }
  if (row.sample_size === 1) {
    return formatDateTime(row.latest_resolved_at);
  }
  return `${formatDateTime(row.earliest_resolved_at)} → ${formatDateTime(row.latest_resolved_at)}`;
}

/**
 * What was deliberately not counted. Rendered per row rather than as a footnote
 * because the exclusions are what make the median honest, and a footnote is
 * read by nobody.
 */
function exclusions(row: M365ServiceMttr): string {
  const parts: string[] = [];
  if (row.excluded_open > 0) {
    parts.push(`${row.excluded_open} still open`);
  }
  if (row.excluded_undated > 0) {
    parts.push(`${row.excluded_undated} undated`);
  }
  return parts.length > 0 ? parts.join(", ") : "none";
}
